import { useEffect, useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Check, Loader2, Search, Send, X } from 'lucide-react';
import { forwardMessages } from '@/api/forward';
import { getInboxCapabilities, searchConversations } from '@/api/inbox';
import type { InboxConversation } from '@/api/inboxTypes';
import { Sheet } from '@/components/Sheet';
import { Avatar } from '@/components/inbox/Avatar';
import { useConversationList, useInboxes, flatten } from '@/hooks/useInboxData';
import type { ChannelCapabilities } from '@/lib/channelRules';
import { channelKind } from '@/lib/channels';
import { cwKeys } from '@/lib/cwCache';
import {
  buildForwardRequest, canHideAuthor, filterRecipients, newChatInboxes, NEW_CHAT_PLACEHOLDER, pluralMessages,
  type ForwardResult, type ForwardTarget,
} from '@/lib/forward';
import { folderToQuery } from '@/lib/inboxQuery';
import { formatRowTime } from '@/lib/messageModel';

interface Props {
  accountId: number;
  /** Исходная переписка. */
  source: InboxConversation;
  /** Номера сообщений в порядке времени. */
  messageIds: number[];
  onClose: () => void;
  /** Ушло: результат сервера и номер переписки, куда переслали. */
  onDone: (result: ForwardResult) => void;
}

/**
 * Окно «Переслать»: выбрать получателя (недавние и поиск по перепискам либо «Новый чат» — канал + ник / ссылка /
 * телефон / почта), по желанию подпись, «Скрыть отправителя» (только внутри одного канала с настоящей пересылкой).
 * Ошибку сервера (422) показываем как есть, окно остаётся открытым.
 */
export function ForwardSheet({ accountId, source, messageIds, onClose, onDone }: Props) {
  const [tab, setTab] = useState<'chats' | 'new'>('chats');
  const [text, setText] = useState('');
  const [debounced, setDebounced] = useState('');
  const [picked, setPicked] = useState<InboxConversation | null>(null);
  const [newInbox, setNewInbox] = useState<number | null>(null);
  const [query, setQuery] = useState('');
  const [comment, setComment] = useState('');
  const [hideAuthor, setHideAuthor] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(text.trim()), 350);
    return () => clearTimeout(t);
  }, [text]);

  const recentQ = useConversationList(accountId, useMemo(() => folderToQuery('all'), []), true);
  const recent = useMemo(() => flatten(recentQ.data), [recentQ.data]);
  const searchQ = useQuery({
    queryKey: ['cw', 'forward-search', accountId, debounced],
    queryFn: () => searchConversations(accountId, debounced),
    enabled: debounced.length >= 2,
    staleTime: 30_000,
    retry: 1,
  });

  // Правила каналов: нужны и для «Нового чата», и для «Скрыть отправителя». Кэш общий со строкой ввода.
  const inboxesQ = useInboxes(accountId, true);
  const inboxes = inboxesQ.data ?? [];
  const capsQs = useQueries({
    queries: inboxes.map((i) => ({
      queryKey: cwKeys.caps(accountId, i.id, 0),
      queryFn: () => getInboxCapabilities(accountId, i.id),
      staleTime: 30 * 60_000,
      retry: 1,
    })),
  });
  const caps = useMemo(() => {
    const m = new Map<number, ChannelCapabilities | null | undefined>();
    inboxes.forEach((i, n) => m.set(i.id, capsQs[n]?.data));
    return m;
  }, [inboxes, capsQs]);
  const newInboxes = useMemo(() => newChatInboxes(inboxes, caps), [inboxes, caps]);

  const searching = text.trim().length > 0;
  const list = useMemo(() => {
    if (!searching) return recent;
    if (debounced.length >= 2 && debounced === text.trim() && searchQ.data) return searchQ.data;
    return filterRecipients(recent, text); // пока ответ не пришёл — ищем среди уже загруженных
  }, [searching, recent, debounced, text, searchQ.data]);
  const searchPending = searching && debounced.length >= 2 && searchQ.isFetching && !searchQ.data;

  const targetInboxId = tab === 'chats' ? picked?.inboxId ?? null : newInbox;
  const showHide = canHideAuthor(source.inboxId, targetInboxId, caps.get(source.inboxId));

  const target: ForwardTarget | null = tab === 'chats'
    ? (picked ? { conversation_id: picked.id } : null)
    : (newInbox !== null && query.trim() ? { inbox_id: newInbox, query: query.trim() } : null);

  const send = async () => {
    if (!target || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await forwardMessages(accountId, buildForwardRequest({
        convId: source.id, messageIds, target, comment, hideAuthor: showHide && hideAuthor,
      }));
      onDone(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось переслать');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet label="Переслать" onClose={onClose} tall>
      <div className="sh">
        <h3>Переслать</h3>
        <span className="shsub">{pluralMessages(messageIds.length)}</span>
      </div>
      <div className="fwd-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'chats'} className={`chip${tab === 'chats' ? ' on' : ''}`} onClick={() => setTab('chats')}>Переписки</button>
        <button type="button" role="tab" aria-selected={tab === 'new'} className={`chip${tab === 'new' ? ' on' : ''}`} onClick={() => setTab('new')}>Новый чат</button>
      </div>

      {tab === 'chats' ? (
        <>
          <label className="ifield fwd-search">
            <span className="sr-only">Поиск по перепискам</span>
            <span className="fwd-sbox">
              <Search aria-hidden />
              <input type="search" value={text} onChange={(e) => setText(e.target.value)} placeholder="Поиск по перепискам" enterKeyHint="search" />
            </span>
          </label>
          <div className="sb fwd-list" role="listbox" aria-label="Переписки">
            {!searching && recentQ.isPending && <div className="empty" role="status"><span className="spinner size-7 inline-block" /></div>}
            {searchPending && <div className="empty" role="status"><span className="spinner size-7 inline-block" /></div>}
            {!searchPending && list.length === 0 && !recentQ.isPending && (
              <p className="empty">{searching ? 'Ничего не найдено' : 'Переписок пока нет'}</p>
            )}
            {!searchPending && list.map((c) => {
              const on = picked?.id === c.id;
              const inboxName = inboxes.find((i) => i.id === c.inboxId)?.name;
              return (
                <button key={c.id} type="button" role="option" aria-selected={on} className={`opt fwd-opt${on ? ' on' : ''}`} onClick={() => setPicked(on ? null : c)}>
                  <Avatar name={c.contact.name} seed={c.contact.id || c.id} channelType={c.channelType} />
                  <span className="t">
                    {c.contact.name}
                    <small>{inboxName ?? channelKind(c.channelType).label}{c.lastActivityAt ? ` · ${formatRowTime(c.lastActivityAt)}` : ''}</small>
                  </span>
                  {on && <Check className="fwd-ck" aria-label="Выбрано" />}
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <div className="sb">
          <div>
            <div className="h4">Канал</div>
            {inboxesQ.isPending || capsQs.some((q) => q.isPending) ? (
              <div className="empty" role="status"><span className="spinner size-7 inline-block" /></div>
            ) : newInboxes.length === 0 ? (
              <p className="pnote">Ни один из ваших каналов не умеет начинать переписку первым.</p>
            ) : (
              <div className="chips" role="radiogroup" aria-label="Канал">
                {newInboxes.map((i) => (
                  <button key={i.id} type="button" role="radio" aria-checked={newInbox === i.id} className={`chip${newInbox === i.id ? ' on' : ''}`} onClick={() => setNewInbox(i.id)}>
                    {i.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          {newInboxes.length > 0 && (
            <label className="ifield">
              <span>Кому</span>
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={NEW_CHAT_PLACEHOLDER} autoCapitalize="none" autoCorrect="off" spellCheck={false} />
            </label>
          )}
        </div>
      )}

      <div className="fwd-foot">
        <label className="ifield">
          <span>Подпись (по желанию)</span>
          <textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Уйдёт отдельным сообщением перед пересланными" />
        </label>
        {showHide && (
          <label className="toggle">
            <input type="checkbox" checked={hideAuthor} onChange={(e) => setHideAuthor(e.target.checked)} />
            <span>Скрыть отправителя</span>
          </label>
        )}
        {error && <p className="ferr" role="alert">{error}</p>}
        <div className="btnrow">
          <button type="button" className="ghost" onClick={onClose} disabled={busy}><X aria-hidden />Отмена</button>
          <button type="button" className="primary" onClick={() => void send()} disabled={!target || busy}>
            {busy ? <Loader2 className="spin-i" aria-hidden /> : <Send aria-hidden />}Переслать
          </button>
        </div>
      </div>
    </Sheet>
  );
}
